import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Avatar,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Fade
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom"; // updated import for router link
import LoginDTO from "../../Common/DTO/LoginDTO";
import { login } from "../../APIs/LoginAPI";
import { IsAuthenticated, getTenants } from "../../Utils/JWTTokenUtil";
import { useMsal } from '@azure/msal-react';
import { EventType } from "@azure/msal-browser";
import { apiRequest } from "../../msalConfig";

import vodafoneLogo from "../../assets/vodafone-logo.png";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { instance } = useMsal();

  const [showSecretLink, setShowSecretLink] = useState(false);
  const [typedKeys, setTypedKeys] = useState("");
  const secretWord = "signup";

  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        const result = event.payload;
        localStorage.setItem('token', result.accessToken);
        localStorage.setItem('email', result.account.username);
        // Set default tenant from token
        const tenants = getTenants();
        if (tenants.length > 0) {
          localStorage.setItem('selectedTenant', tenants[0]);
        }
      }
    });
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  // Secret word listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (typeof e.key !== "string") return;
      const key = e.key.toLowerCase();
      const newTypedKeys = (typedKeys + key).slice(-secretWord.length);
      setTypedKeys(newTypedKeys);

      if (newTypedKeys === secretWord) {
        setShowSecretLink(true);
        setTypedKeys("");
        setTimeout(() => setShowSecretLink(false), 3000); // hide after 3 seconds
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [typedKeys]);

  if (IsAuthenticated()) {
    return navigate("/home");
  }

  const handleSso = async () => {
    instance.loginRedirect({
      scopes: apiRequest.scopes
    }).catch(e => console.error(e));
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const response = await login(new LoginDTO(email, password));
      localStorage.setItem("token", response.token);
      localStorage.setItem("email", email);
      // Set default tenant from token
      const tenants = getTenants();
      if (tenants.length > 0) {
        localStorage.setItem('selectedTenant', tenants[0]);
      }
      navigate("/home");
    } catch {
      setError("Login failed. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        pt: 20,
        backgroundColor: "#f5f5f5",
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 4,
          maxWidth: 400,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Logo */}
        <Avatar
          src={vodafoneLogo}
          alt="Vodafone Logo"
          sx={{ width: 60, height: 60, mb: 1 }}
        />
        <Typography variant="h6" component="h1" mb={3} textAlign="center">
          Process Automation Framework
        </Typography>

        <Button onClick={handleSso} variant="contained" fullWidth>
          Log in with Vodafone
        </Button>
        <hr />

        {/* Error */}
        {error && (
          <Fade in={!!error}>
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          </Fade>
        )}

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
          />
          <Button type="submit" variant="contained" fullWidth>
            Log In
          </Button>
        </Box>

        {/* Secret Sign up link */}
        {showSecretLink && (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2">
              Don’t have an account?{" "}
              <Link
                component={RouterLink}
                to="/signup"
                state={{ allowed: true }} // pass allowed flag
                underline="hover"
              >
                Sign up here
              </Link>
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default Login;
